import { useT } from '../i18n';
import {
  STARE_VALUES,
  SITUATIE_JURIDICA_VALUES,
  MAX_PHOTOS,
  MAX_PHOTO_SIZE_MB,
  type ConditionInfo,
  type SelectedFile,
  type SituatieJuridica,
  type Stare,
  type SubmissionFiles,
} from '../domain/types';
import { validatePhoto, validatePlan, type Errors } from '../lib/validation';
import { toSelectedPhoto, toSelectedPlan } from '../lib/imageProcessing';
import { Field } from './Field';
import { PhotoInput } from './PhotoInput';
import './StepForm.css';

interface StepConditionProps {
  condition: ConditionInfo;
  files: SubmissionFiles;
  errors: Errors;
  onConditionChange: (update: Partial<ConditionInfo>) => void;
  onAddPhotos: (entries: SelectedFile[]) => void;
  onRemovePhoto: (id: string) => void;
  onSetPlan: (entries: SelectedFile[]) => void;
  onRemovePlan: () => void;
  onPhotoError: (message: string | null) => void;
  onPlanError: (message: string | null) => void;
}

export function StepCondition({
  condition,
  files,
  errors,
  onConditionChange,
  onAddPhotos,
  onRemovePhoto,
  onSetPlan,
  onRemovePlan,
  onPhotoError,
  onPlanError,
}: StepConditionProps) {
  const t = useT();

  const stareLabels: Record<Stare, string> = {
    renovat_recent: t.step2.stareOptions.renovat_recent,
    renovat_invechit: t.step2.stareOptions.renovat_invechit,
    locuibil_nerenovat: t.step2.stareOptions.locuibil_nerenovat,
    necesita_renovare_completa: t.step2.stareOptions.necesita_renovare_completa,
  };

  const situatieLabels: Record<SituatieJuridica, string> = {
    unic: t.step2.situatieOptions.unic,
    coproprietari: t.step2.situatieOptions.coproprietari,
    succesiune: t.step2.situatieOptions.succesiune,
    sarcini: t.step2.situatieOptions.sarcini,
    nustiu: t.step2.situatieOptions.nustiu,
  };

  return (
    <div className="step-form">
      <header className="step-form__intro">
        <h2>{t.step2.heading}</h2>
        <p className="lede">{t.step2.lede}</p>
      </header>

      <Field name="stareActuala" label={t.step2.stareLabel} error={errors['stareActuala']}>
        {(props) => (
          <select
            {...props}
            className="select"
            value={condition.stareActuala}
            onChange={(e) => onConditionChange({ stareActuala: e.target.value as Stare | '' })}
          >
            <option value="">{t.step2.starePlaceholder}</option>
            {STARE_VALUES.map((value) => (
              <option key={value} value={value}>
                {stareLabels[value]}
              </option>
            ))}
          </select>
        )}
      </Field>

      {/* The centerpiece. Photos are recommended, not required — the gate never
          checks them and step 3 only nudges. */}
      <PhotoInput
        name="photos"
        label={t.step2.photosLabel}
        help={t.step2.photosHelp}
        meta={t.step2.photosMeta(MAX_PHOTOS, MAX_PHOTO_SIZE_MB)}
        max={MAX_PHOTOS}
        cta={t.step2.photosCta}
        dropHint={t.step2.photosDrop}
        files={files.photos}
        error={errors['photos']}
        validate={(file) => validatePhoto(file, t)}
        process={toSelectedPhoto}
        onAdd={onAddPhotos}
        onRemove={onRemovePhoto}
        onReject={onPhotoError}
      />

      <PhotoInput
        name="plan"
        label={t.step2.planLabel}
        help={t.step2.planHelp}
        optional
        max={1}
        cta={t.step2.planCta}
        ctaReplace={t.step2.planCtaReplace}
        dropHint={t.step2.planDrop}
        files={files.plan ? [files.plan] : []}
        error={errors['plan']}
        validate={(file) => validatePlan(file, t)}
        process={toSelectedPlan}
        onAdd={onSetPlan}
        onRemove={() => onRemovePlan()}
        onReject={onPlanError}
      />

      <Field
        name="situatieJuridica"
        label={t.step2.situatieLabel}
        help={t.step2.situatieHelp}
        error={errors['situatieJuridica']}
      >
        {(props) => (
          <select
            {...props}
            className="select"
            value={condition.situatieJuridica}
            onChange={(e) =>
              onConditionChange({ situatieJuridica: e.target.value as SituatieJuridica | '' })
            }
          >
            <option value="">{t.step2.situatiePlaceholder}</option>
            {SITUATIE_JURIDICA_VALUES.map((value) => (
              <option key={value} value={value}>
                {situatieLabels[value]}
              </option>
            ))}
          </select>
        )}
      </Field>

      <Field name="intentieNote" label={t.step2.noteLabel} help={t.step2.noteHelp}>
        {(props) => (
          <textarea
            {...props}
            className="textarea"
            rows={4}
            placeholder={t.step2.notePlaceholder}
            value={condition.intentieNote}
            onChange={(e) => onConditionChange({ intentieNote: e.target.value })}
          />
        )}
      </Field>
    </div>
  );
}
